import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Zap, Cpu, MapPin, LifeBuoy } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";

const FACTS = [
  { icon: Zap, k: "Alles aus einer Hand", t: "Pooltechnik und Elektroinstallation" },
  { icon: Cpu, k: "100 % Eigenentwicklung", t: "Jede Poolsteuerung entsteht im eigenen Haus" },
  { icon: MapPin, k: "Ganz Deutschland", t: "Einsatzgebiet" },
  { icon: LifeBuoy, k: "Notdienst 24/7", t: "Für Bestandskunden rund um die Uhr" },
];

const Fact = ({ f, i }) => (
  <Reveal delay={i * 0.07} className="bg-[var(--ink)]">
    <div data-testid={`company-fact-${i}`} className="group flex h-full flex-col gap-6 p-6 sm:p-8 transition-colors duration-500 hover:bg-[#0A2E33]">
      <f.icon className="h-6 w-6 text-[var(--lagoon)] transition-transform duration-500 group-hover:-translate-y-1" />
      <div>
        <p className="font-display text-xl sm:text-2xl font-medium tracking-[-0.02em]">{f.k}</p>
        <p className="mt-1 text-sm text-[var(--mist)]">{f.t}</p>
      </div>
    </div>
  </Reveal>
);

export const Company = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-10%", "10%"]);

  return (
    <section id="unternehmen" ref={ref} data-testid="company-section" className="relative mx-auto max-w-[1400px] px-5 py-24 sm:px-8 sm:py-32">
      <div className="grid items-end gap-12 lg:grid-cols-12">
        <div className="lg:col-span-6">
          <Reveal><Eyebrow>Das Unternehmen dahinter</Eyebrow></Reveal>
          <Reveal delay={0.08}>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-semibold leading-[1] tracking-[-0.03em]">
              the coolpool company <span className="font-accent font-normal text-[var(--lagoon)]">GmbH.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-[var(--mist)]">
              <span className="text-[var(--foam)]">Wasser und Technik perfekt vereint.</span> Aus Gütersloh verbinden wir exklusiven Poolbau, intelligente Schwimmbadtechnik und kristallklare Wasserqualität – mit Erfahrung aus unzähligen Projekten. REVIVO-Smartpool bringt genau dieses Know-how in Ihren bestehenden Pool.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <blockquote className="mt-8 border-l-2 border-[var(--lagoon)] pl-5 font-accent text-2xl sm:text-3xl text-[var(--foam)]">
              „Man wächst mit seinen Aufgaben. Wir lassen Ihre Poolträume Wirklichkeit werden.“
            </blockquote>
          </Reveal>
          <Reveal delay={0.26} className="mt-10 flex flex-col gap-3 sm:flex-row">
            <a href="https://cool-pool-company.de/" target="_blank" rel="noopener noreferrer" data-testid="company-website-btn" className="btn-primary justify-center">
              cool-pool-company.de besuchen <ArrowUpRight className="h-4 w-4" />
            </a>
            <a href="https://konfigurator.cool-pool-company.de/" target="_blank" rel="noopener noreferrer" data-testid="company-configurator-btn" className="btn-ghost justify-center">
              Zum Pool-Konfigurator <ArrowUpRight className="h-4 w-4" />
            </a>
          </Reveal>
        </div>
        <Reveal delay={0.1} className="lg:col-span-6">
          <div className="relative aspect-[4/3] overflow-hidden rounded-[28px] rounded-tl-[160px] spotlight">
            <motion.img style={{ y }} src="/images/coolpool-gartenpool.webp" alt="Beleuchteter Gartenpool am Abend" className="absolute inset-0 h-[120%] w-full -top-[10%] object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#04171B]/70 via-transparent to-transparent" />
            <p className="absolute bottom-5 left-5 rounded-full border border-white/15 bg-[rgba(4,23,27,0.6)] px-4 py-2 font-mono text-[11px] uppercase tracking-[0.25em] text-[var(--foam)] backdrop-blur-md">
              Auf'm Reck 65 · 33334 Gütersloh
            </p>
          </div>
        </Reveal>
      </div>
      <div className="mt-16 grid gap-px overflow-hidden rounded-[28px] border border-white/10 bg-white/10 sm:grid-cols-2 lg:grid-cols-4">
        {FACTS.map((f, i) => <Fact key={f.k} f={f} i={i} />)}
      </div>
    </section>
  );
};
